import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil } from 'lucide-react';

// Distancia euclidiana simple entre dos puntos lat/lng (suficiente para matching)
const distanciaCoords = (lat1, lng1, lat2, lng2) =>
    Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lng1 - lng2, 2));

function Ordenamiento({
    caseta,
    index,
    rutaSeleccionada,
    tipoVehiculo,
    handleDeleteCaseta,
    handleConsecutivoChange,
    formatearEnteros,
    switchTipoVehiculo,
    onOrdenamientoCompleto,
    onEditCaseta
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
        setActivatorNodeRef
    } = useSortable({
        id: caseta.ID_Caseta
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragging ? 'grabbing' : 'default',
    };

    // Semáforo: compara el costo TUSA con el costo de la caseta INEGI más cercana
    const rowClass = useMemo(() => {
        const casetasINEGI = rutaSeleccionada[1] || [];
        if (!casetasINEGI.length) return '';

        const tusaLat = parseFloat(caseta.latitud);
        const tusaLng = parseFloat(caseta.longitud);
        if (isNaN(tusaLat) || isNaN(tusaLng)) return '';

        let minDist = Infinity;
        let closestINEGI = null;
        for (const inegi of casetasINEGI) {
            try {
                const coords = JSON.parse(inegi.geojson).coordinates;
                const dist = distanciaCoords(tusaLat, tusaLng, coords[1], coords[0]);
                if (dist < minDist) {
                    minDist = dist;
                    closestINEGI = inegi;
                }
            } catch (_) { }
        }

        // Si la caseta más cercana está a más de ~0.5° (~55 km), no hay match confiable
        if (!closestINEGI || minDist > 0.5) return '';

        const costoTUSA = parseFloat(caseta[switchTipoVehiculo(tipoVehiculo).replaceAll(' ', '')] || 0);
        const costoINEGI = parseFloat(closestINEGI.costo_caseta || 0);

        if (costoTUSA === costoINEGI) return 'table-success';
        if (costoTUSA > costoINEGI) return 'table-warning';
        return 'table-danger';
    }, [caseta, rutaSeleccionada, tipoVehiculo, switchTipoVehiculo]);

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`tabla-ordenamiento text-center py-1 ${rowClass}`}
        >
            {/* Celda con el handle de drag */}
            <td
                ref={setActivatorNodeRef}
                className="drag-handle py-1 tabla-ordenamiento"
                style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    padding: '0.5rem',
                    verticalAlign: 'middle',
                    userSelect: 'none'
                }}
                {...attributes}
                {...listeners}
            >
                <GripVertical size={18} className="text-muted" />
            </td>

            {/* ID Caseta */}
            <td className='text-right py-1 tabla-ordenamiento'>
                {caseta.ID_Caseta}{' '}
                <span
                    style={{ cursor: 'help' }}
                    title={caseta.IAVE ? 'SI Acepta el pago con TAG' : 'NO admite el pago con TAG'}
                >
                    {caseta.IAVE ? '✅' : '❌'}
                </span>
            </td>

            {/* Nombre */}
            <td className='py-1 tabla-ordenamiento'>
                {caseta.Nombre}
                {onEditCaseta && (
                    <button
                        className="btn btn-sm btn-link p-0 ml-1"
                        style={{ verticalAlign: 'middle', color: '#4e73df' }}
                        title="Editar costos de la caseta"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEditCaseta(caseta.ID_Caseta, caseta.ID_Pase || null);
                        }}
                    >
                        <Pencil size={13} />
                    </button>
                )}
            </td>

            {/* Estado - con onClick funcional */}
            <td
                className='py-1 tabla-ordenamiento'>
                {caseta.Estado}
            </td>

            {/* Latitud */}
            <td className='py-1 tabla-ordenamiento'>{caseta.latitud}</td>

            {/* Longitud */}
            <td className='py-1 tabla-ordenamiento'>{caseta.longitud}</td>

            {/* Precio */}
            <td className='py-1 tabla-ordenamiento'>
                $ {formatearEnteros(caseta[switchTipoVehiculo(tipoVehiculo).replaceAll(" ", "")])}
            </td>

            {/* Consecutivo y acciones */}
            <td className='d-flex flex-row-reverse py-1 tabla-ordenamiento'>
                <button
                    className="btn btn-sm btn-outline-danger"
                    style={{ float: 'right' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCaseta(caseta.ID);
                    }}
                    title={'Retirar caseta de la ruta TUSA.'}
                >
                    <i className="fas fa-trash"></i>
                </button>
                <input
                    style={{
                        maxWidth: '3rem',
                        textAlign: 'center',
                        marginRight: '0.5rem'
                    }}
                    className="form-control form-control-sm "
                    maxLength={2}
                    disabled={true}
                    type="text"
                    name="txtCasetaConsecutivo"
                    id={`txtCasetaConsecutivo_${caseta.ID_Caseta}`}
                    value={caseta.consecutivo || ''}
                    onChange={(e) => {
                        e.stopPropagation();
                        const valor = e.target.value;
                        // Solo permitir números
                        if (valor === '' || /^\d+$/.test(valor)) {
                            handleConsecutivoChange(caseta.ID_Caseta, valor);
                            if (onOrdenamientoCompleto) {
                                onOrdenamientoCompleto();
                            }
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                />
            </td>
        </tr>
    );
}

export default Ordenamiento;